import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { format } from "date-fns";

import ReactTable from "components/lib/Global/ReactTable";
import Sidebar from "components/Applications/Sidebar";
import HeroSection from "components/Applications/HeroSection";
import OrgAdminDashboardWrapper from "components/lib/Global/OrgAdminDashboardWrapper";
import SearchInput from "components/lib/Shared/SearchInput";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { getCompaniesAsync } from "state/slices/companies";
import SubRowAsync from "./SubRow";
import { Constants } from "config/constants";
import GoBackToHomeBtn from "../GoBackToHomeBtn";
import { getCompanyNameWithSuffix } from "utils/company";
import { getCompaniesTransactionsListAsync } from "state/slices/companiesTransactions";

export default function Pending() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();

  const role = Cookies.get("role");
  const [statuses, setStatuses] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const { getCompaniesTransactionsList, companiesTransactionsList } =
    useSelector((state) => state.companiesTransactionsSlice);
  const { orgAdmin } = useSelector((state) => state.roleswitchSlice);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (role === Constants.COOKIE_ROLES.ORG_ADMIN) {
      setStatuses([{ value: "Admin Deferred", label: "Admin Deferred" }]);
    } else {
      setStatuses([
        { value: "Submission Deferred", label: "Submission Deferred" },
      ]);
    }
    setIsMounted(true);
  }, [role]);

  useEffect(() => {
    if (isAuthenticated && isMounted) {
      if (debouncedSearchTerm) setCurrentPage(1);
      dispatch(
        getCompaniesTransactionsListAsync({
          queryParams: {
            limit: rowsPerPage,
            skip: (currentPage - 1) * rowsPerPage,
            search: debouncedSearchTerm,
            status:
              statuses.length > 0
                ? statuses.map((status) => status.value)
                : null,
            order: "asc",
            orgAdmin: orgAdmin ? orgAdmin : "",
          },
        })
      );
    }
  }, [
    isAuthenticated,
    rowsPerPage,
    currentPage,
    debouncedSearchTerm,
    statuses,
    isMounted,
    location.search,
  ]);

  const currentUser = useSelector((state) => state.userSlice);

  const refreshTableData = async () => {
    let statuses = [];
    if (role === Constants.COOKIE_ROLES.ORG_ADMIN) {
      statuses = ["Admin Deferred"];
    } else {
      statuses = ["Submission Deferred"];
    }
    dispatch(
      getCompaniesTransactionsListAsync({
        queryParams: {
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          search: debouncedSearchTerm,
          status: statuses,
          order: "asc",
          orgAdmin: orgAdmin ? orgAdmin : "",
        },
      })
    );
  };

  // this function will render our row sub components
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <SubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          refreshTableData={refreshTableData}
        />
      );
    },
    []
  );

  if (!currentUser?._id || getUserStatus.status === "loading") {
    return (
      <OrgAdminDashboardWrapper>
        <div className="flex justify-center mx-auto mt-10 text-white">
          <SpinnerIcon className="text-gray-400" />
        </div>
      </OrgAdminDashboardWrapper>
    );
  }

  const columns = [
    {
      Header: "Batch No.",
      accessor: "bachNo",
      Cell: ({ row }) => "-",
    },
    {
      Header: "Name",
      accessor: "companyName",
      className: "p-4 whitespace-normal max-w-[150px]",
      Cell: ({ row }) => getCompanyNameWithSuffix(row.original),
    },
    {
      Header: "Application Type",
      accessor: "applicationType",
      Cell: ({ row }) => {
        if (row.original?.transferYourBusinessInitiator) {
          return "Transfer Your Business";
        }
        return "Incorporation";
      },
    },
    {
      Header: "Change Type",
      accessor: "type",
      Cell: ({ row }) => row.original.type,
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };

        return capitalize(original?.transactionType) || "Automatic";
      },
    },
    {
      Header: "Tnx No.",
      accessor: "tnxNo",
      Cell: ({ row }) => "-",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => {
        if (row.original.status.includes("II")) {
          return (
            <>
              {row.original.status}{" "}
              <span className="items-center gap-x-1.5 rounded-full px-2 py-1 text-xxs font-medium text-gray-900 ring-1 ring-inset ring-green-primary bg-green-100/25 tracking-wide">
                Corrected
              </span>
            </>
          );
        }
        if (
          ["Admin Review", "Submission", "BRS Review"].includes(
            row.original.status
          )
        ) {
          return (
            <>
              {row.original.status}{" "}
              <span className="items-center gap-x-1.5 rounded-full px-2 py-1 text-xxs font-medium text-gray-900 ring-1 ring-inset ring-primary-200 bg-purple-100/25 tracking-wide">
                New
              </span>
            </>
          );
        }
        return row.original.status;
      },
    },
    {
      Header: "Date",
      Cell: ({ row }) =>
        row.original?.createdAt
          ? format(new Date(row.original?.createdAt), "yyyy/MM/dd")
          : "N/A",
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        return (
          <button
            type="button"
            {...row?.getToggleRowExpandedProps()}
            className={`text-gray-900 group flex rounded-md items-center w-full px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500`}
          >
            {row.isExpanded ? "Close" : "View"}
          </button>
        );
      },
    },
  ];

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  return (
    <OrgAdminDashboardWrapper>
      <div className="grid gap-4 py-10 mx-auto">
        <HeroSection />
        <GoBackToHomeBtn />
        <div className="grid grid-cols-6 border-t border-gray">
          <div className="col-span-6 pr-8 md:col-span-1">
            <Sidebar />
          </div>
          <div className="col-span-6 md:border-l md:col-span-5 md:pl-8 pt-8 border-gray">
            <div className="mb-6">
              <h1 className="text-title5">Deferred Applications</h1>
            </div>
            <div className="flex items-center justify-between my-2">
              <SearchInput value={searchTerm} handleOnChange={handleSearch} />
            </div>
            {companiesTransactionsList.totalCount > 0 ? (
              <ReactTable
                columns={columns}
                data={companiesTransactionsList.items}
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalRows={companiesTransactionsList.totalCount}
                setCurrentPage={setCurrentPage}
                paginate
                renderRowSubComponent={renderRowSubComponent}
                isLoading={getCompaniesTransactionsList.status === "loading"}
              />
            ) : (
              <p className="text-2xl text-center text-primary">
                No applications available.
              </p>
            )}
          </div>
        </div>
      </div>
    </OrgAdminDashboardWrapper>
  );
}
